import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Department kicker — small-caps emerald, set in Roboto Mono.
function Kicker({ children, className = "" }) {
    return <span className={`eyebrow block text-accent ${className}`}>{children}</span>;
}

// A story is a white sheet ruled off at the top in emerald. No fill washes,
// no drop shadow — the tinted band behind it supplies the contrast.
export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up bg-surface border border-line border-t-[3px] border-t-accent"
            >
                <div className="p-6 sm:p-8">
                    <Kicker>{label}</Kicker>
                    <h2 className="font-display text-[1.9rem] sm:text-4xl lg:text-[3rem] font-bold leading-[1.14] mt-3 text-foreground group-hover:text-accent transition-colors duration-200">
                        {article.title}
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mt-6">
                        <div className="lg:col-span-7 overflow-hidden bg-surface-raised aspect-[16/10]">
                            {article.thumbnail && (
                                <img
                                    src={article.thumbnail}
                                    alt={article.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                />
                            )}
                        </div>
                        <div className="lg:col-span-5 flex flex-col">
                            {article.excerpt && (
                                <p className="text-[15px] text-dim leading-relaxed border-t border-line pt-4 lg:border-t-0 lg:pt-0">
                                    {article.excerpt}
                                </p>
                            )}
                            <div className="mt-5 lg:mt-auto pt-4 border-t border-line font-mono text-[11px] uppercase tracking-wider text-dim">
                                {article.author && <div className="truncate text-foreground">{article.author}</div>}
                                <div className="mt-1">{timeAgo(article.published_at)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 py-4 border-b border-line"
            >
                <div className="flex-1 min-w-0">
                    <Kicker className="!text-[10px]">{label}</Kicker>
                    <h3 className="font-display text-[16px] font-semibold leading-[1.3] mt-1.5 text-foreground group-hover:text-accent transition-colors duration-150 line-clamp-3">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-dim mt-2 block">
                        {timeAgo(article.published_at)}
                    </span>
                </div>
                {article.thumbnail && (
                    <div className="w-[92px] h-[70px] shrink-0 overflow-hidden bg-surface-raised border border-line">
                        <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group flex flex-col h-full animate-fade-up bg-surface border border-line border-t-2 border-t-accent transition-colors duration-200 hover:border-line-strong"
        >
            <div className="aspect-[3/2] overflow-hidden bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="flex flex-col flex-1 p-5">
                <Kicker className="!text-[10px]">{label}</Kicker>
                <h3 className="font-display text-[1.15rem] font-semibold leading-[1.28] mt-2 text-foreground group-hover:text-accent transition-colors duration-150">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-2.5 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                )}
                <div className="mt-auto pt-4 border-t border-line flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-dim">
                    {article.author && <span className="truncate max-w-[160px]">{article.author}</span>}
                    {article.author && <span className="text-dim">/</span>}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
